/**
 * TINY SEEDED RNGs — the same seed always yields the same sequence. Used wherever a roll must be
 * reproducible: save.ts's gear migration (runs on every load and must not re-roll), and every baked
 * render (plants, racks, ground, floor tiles), where the sequence IS the art.
 *
 * One LCG (`s * 1664525 + 1013904223`, mod 2^32), two SEED SCRAMBLES. They are NOT interchangeable:
 * each call site's seed is part of its look, and moving a site from one variant to the other reshuffles
 * every mark it draws. Returns floats in [0, 1].
 */
export type Rng = () => number;

/** One LCG step on a uint32 state. Exposed for one-shot hashes that need a single roll, not a stream. */
export function lcgNext(s: number): number {
  return (s * 1664525 + 1013904223) >>> 0;
}

/** SCRAMBLED seed: the seed is multiplied by Knuth's golden-ratio constant first, so neighbouring
 *  small seeds (1, 2, 3…) start far apart. Plants, plant physics/fx, the backdrop forest, save migration. */
export function makeRng(seed: number): Rng {
  let s = (seed * 2654435761) >>> 0;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 0xffffffff; };
}

/** RAW seed: the seed is used as-is (only coerced to uint32). For sites whose seed is already a mixed
 *  constant or hash — floor tiles, the grass brush tile, the ground's sward/water/bank/tuft scatters. */
export function makeRngRaw(seed: number): Rng {
  let s = seed >>> 0;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 0xffffffff; };
}
